import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Popconfirm, message, Modal, Form, Select, DatePicker, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const WorkflowDelegates: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [delegates, setDelegates] = useState<workflowService.WorkflowDelegate[]>([]);
  const [definitions, setDefinitions] = useState<workflowService.WorkflowDefinition[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    loadDefinitions();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getMyDelegates();
      if (res.data) {
        setDelegates(res.data as workflowService.WorkflowDelegate[]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDefinitions = async () => {
    try {
      const res = await workflowService.listDefinitions({ status: 1, pageSize: 100 });
      if (res.data) {
        setDefinitions(res.data.list || []);
      }
    } catch {}
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const submitDelegate = async () => {
    try {
      const values = await form.validateFields();
      await workflowService.createDelegate({
        delegateToId: values.delegateToId,
        definitionKey: values.definitionKey || '',
        startTime: values.dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endTime: values.dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
        reason: values.reason,
      });
      message.success('委托配置成功');
      setModalVisible(false);
      loadData();
    } catch {
      message.error('配置失败');
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await workflowService.cancelDelegate(id);
      message.success('取消成功');
      loadData();
    } catch {
      message.error('取消失败');
    }
  };

  const columns: ColumnsType<workflowService.WorkflowDelegate> = [
    { title: '被委托人', dataIndex: 'delegateToId', key: 'delegateToId' },
    {
      title: '流程类型',
      dataIndex: 'definitionKey',
      key: 'definitionKey',
      render: (key: string) => key || '所有流程',
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => <Tag color={status === 1 ? 'success' : 'default'}>{status === 1 ? '有效' : '无效'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        record.status === 1 && (
          <Popconfirm title="确定要取消此委托吗？" onConfirm={() => handleCancel(record.id)}>
            <Button size="small" danger>取消</Button>
          </Popconfirm>
        )
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="委托管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建委托</Button>}>
        <Table columns={columns} dataSource={delegates} rowKey="id" loading={loading} pagination={false} />
      </Card>

      <Modal title="新建委托" open={modalVisible} onCancel={() => setModalVisible(false)} onOk={submitDelegate}>
        <Form form={form} layout="vertical">
          <Form.Item name="delegateToId" label="委托给" rules={[{ required: true, message: '请选择被委托人' }]}>
            <Select placeholder="选择被委托人">
              <Select.Option value="1">管理员</Select.Option>
              <Select.Option value="2">张三</Select.Option>
              <Select.Option value="3">李四</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="definitionKey" label="流程类型">
            <Select placeholder="选择流程类型（不选则委托所有流程）" allowClear>
              {definitions.map(d => (
                <Select.Option key={d.key} value={d.key}>{d.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="委托时间" rules={[{ required: true, message: '请选择委托时间' }]}>
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="委托原因">
            <TextArea rows={3} placeholder="请输入委托原因" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkflowDelegates;
